import { Col, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatCurrencyComponent } from "../lib/numbers";

const INHERITED_TOOLTIP =
  "This value is inherited from the firm default. To override it, edit the firm's Model / Trading settings.";

// Shared formatting for inherited firm-default MTAX values so every surface
// (account page, household modal, modal banner) renders them identically.
export const formatFirmRate = (value: number | null): string =>
  value != null ? `${value}%` : "Not set";

export const formatFirmBudget = (value: number | null) =>
  value != null ? formatCurrencyComponent(value) : "Not set";

type FirmDefaultMtaxRatesProps = {
  stGainTaxRate: number | null;
  ltGainTaxRate: number | null;
  taxBudget: number | null;
  // "cols" matches AccountModelTrading's full-width layout; "compact" fits a
  // table cell in AccountLotSelectionModal.
  variant: "cols" | "compact";
  // Disambiguates tooltip ids when several instances render on one page.
  idPrefix: string;
};

/**
 * Read-only display of the firm-default MTAX tax rates inherited by an account
 * that does not override the lot-selection method. Extracted so the account
 * page and the household lot-selection modal share one implementation.
 */
const FirmDefaultMtaxRates = ({
  stGainTaxRate,
  ltGainTaxRate,
  taxBudget,
  variant,
  idPrefix,
}: FirmDefaultMtaxRatesProps) => {
  const fields = [
    {
      key: "st-rate",
      colsLabel: "ST Capital Gains Rate",
      compactLabel: "ST Rate",
      value: formatFirmRate(stGainTaxRate),
    },
    {
      key: "lt-rate",
      colsLabel: "LT Capital Gains Rate",
      compactLabel: "LT Rate",
      value: formatFirmRate(ltGainTaxRate),
    },
    {
      key: "tax-budget",
      colsLabel: "Tax Budget",
      compactLabel: "Tax Budget",
      value: formatFirmBudget(taxBudget),
    },
  ];

  if (variant === "compact") {
    return (
      <div className="d-flex gap-2">
        {fields.map((field) => (
          <div key={field.key}>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`${idPrefix}-${field.key}-tooltip`}>
                  {INHERITED_TOOLTIP}
                </Tooltip>
              }
            >
              <Form.Label className="small mb-1 text-decoration-underline">
                {field.compactLabel}
              </Form.Label>
            </OverlayTrigger>
            <div className="form-control-plaintext form-control-sm py-0">
              {field.value}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {fields.map((field) => (
        <Col lg={4} key={field.key}>
          <Form.Group>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`${idPrefix}-${field.key}-tooltip`}>
                  {INHERITED_TOOLTIP}
                </Tooltip>
              }
            >
              <Form.Label className="text-decoration-underline">
                {field.colsLabel}
              </Form.Label>
            </OverlayTrigger>
            <div className="form-control-plaintext">{field.value}</div>
          </Form.Group>
        </Col>
      ))}
    </>
  );
};

export default FirmDefaultMtaxRates;
